import { useAuth } from "@clerk/react"
import { useQuery } from "@tanstack/react-query"
import {
	CACHE_GC_TIME,
	CACHE_REFETCH,
	CACHE_STALE_TIME,
} from "@/config/constants"
import { useMarketplaceOrpc } from "@/integrations/orpc"

/** Root key of every seller coupon campaign query; invalidate it after a decision. */
export function couponCampaignsQueryKey() {
	return ["marketplace-admin", "coupon-campaigns"] as const
}

/** Key of a single coupon campaign. */
export function couponCampaignQueryKey(id: string) {
	return [...couponCampaignsQueryKey(), "detail", id] as const
}

/** A single coupon campaign with its conversation and the shop's context. */
export function useCouponCampaign(id: string) {
	const { client } = useMarketplaceOrpc()
	const { isSignedIn } = useAuth()

	return useQuery({
		queryKey: couponCampaignQueryKey(id),
		queryFn: () => client.couponCampaigns.get({ id }),
		enabled: !!id && !!isSignedIn,
		staleTime: CACHE_STALE_TIME.ENTITY_DETAILS_PAGES,
		gcTime: CACHE_GC_TIME.ENTITY_DETAILS_PAGES,
		refetchOnWindowFocus: CACHE_REFETCH.ON_WINDOW_FOCUS,
	})
}

/** How many coupon requests wait for staff (`pending`; `needs_info` waits for the seller). */
export function usePendingCouponCampaignCount() {
	const { client } = useMarketplaceOrpc()
	const { isSignedIn } = useAuth()

	return useQuery({
		queryKey: [...couponCampaignsQueryKey(), "pending-count"],
		queryFn: async () => {
			const res = await client.couponCampaigns.pendingCount()
			return res.count
		},
		enabled: !!isSignedIn,
		staleTime: CACHE_STALE_TIME.ENTITY_TABLES,
		gcTime: CACHE_GC_TIME.ENTITY_TABLES,
		refetchOnWindowFocus: CACHE_REFETCH.ON_WINDOW_FOCUS,
	})
}
